/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IonPage,
  IonContent,
  IonLoading,
  IonAlert,
} from "@ionic/react";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useAuthInit } from "../Auth";
import {
  requestID,
  baseUrl,
  setUserSession,
  setBusinessData,
  config,
  APP_VERSION,
  APP_COPYRIGHT,
} from "../common/utils";
import { useBusinessContext } from "../context/business-context";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Rating,
  Avatar,
  styled,
  InputBase,
  FormControl,
  InputLabel,
  OutlinedInput,
  Link as MuiLink,
  FormHelperText,
  createTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  PersonOutline,
  LockOutlined,
  HowToReg,
  HelpOutline,
  Login,
  RocketLaunch,
  Store,
  Groups,
  Event,
  AccountBalance,
  TrendingUp,
  Apps,
  Handyman,
  ArrowBack,
  Check,
  Star,
  CalendarMonth,
  Payments,
  Security,
  Menu as MenuIcon,
  Close as CloseIcon,
  ContentCopy,
  Devices,
  ErrorOutline,
  LockPerson,
  Refresh,
  School,
  Hotel,
  Phone,
  Email,
  WhatsApp,
  ArrowForward,
  FormatQuote,
  VerifiedUser,
  ArrowCircleRight,
  ArrowCircleLeft,
  PlayArrow,
  Pause,
  Visibility,
  VisibilityOff,
  LocationOn,
  VpnKey,
  ConfirmationNumber,
  Assignment,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { keyframes, ThemeProvider } from "@emotion/react";
import { generateDeviceId } from "../common/deviceFingerprint";
import { deviceApi } from "../common/deviceApi";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a", // deep navy from spec
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#1f2937",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "12px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching DeltaGovPortal
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  height: "90px",
  padding: "0 40px",
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
}));

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const StyledCard = styled(Paper)({
  width: "450px",
  maxWidth: "100%",
  backgroundColor: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  margin: "0 auto",
});

const VerifyButton = styled(Button)({
  height: "45px",
  backgroundColor: "#243b5a",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  borderRadius: "6px",
  padding: "0 16px",
  "&:hover": {
    backgroundColor: "#162c47",
  },
});

const StyledFormControl = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    height: "42px",
    backgroundColor: "#f9fafb",
    "& fieldset": {
      borderColor: "#d1d5db",
      borderRadius: "6px",
    },
    "&:hover fieldset": {
      borderColor: "#d1d5db",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#243b5a",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    color: "#374151",
    fontWeight: 500,
    transform: "translate(12px, -8px) scale(0.85)",
    "&.Mui-focused": {
      color: "#374151",
    },
  },
});

// Device Check Dialog Component (kept as is)
const DeviceCheckDialog = ({
  open,
  deviceCode,
  deviceHash,
  loading,
  onRetry,
  onLogout
}: {
  open: boolean;
  deviceCode: string;
  deviceHash: string;
  loading: boolean;
  onRetry: () => void;
  onLogout: () => void;
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'error.main'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
          color: 'white',
          py: 2,
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ErrorOutline />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              🔒 DEVICE VERIFICATION REQUIRED
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ErrorOutline
              sx={{
                fontSize: 60,
                color: 'error.main',
                mb: 2,
                filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
              }}
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'error.dark', mb: 1 }}>
              Access Blocked - Device Not Registered
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This device is not authorized to access the system
            </Typography>
          </Box>

          <Alert
            severity="error"
            icon={<LockPerson fontSize="large" />}
            sx={{
              mb: 3,
              textAlign: 'left',
              fontSize: '1.1rem',
              py: 2
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ SECURITY RESTRICTION
            </Typography>
            Your device is not registered. For security reasons, administrative accounts require device registration.
            Your access has been blocked until this device is registered by an administrator.
          </Alert>

          <Box
            sx={{
              mb: 3,
              p: 3,
              bgcolor: 'warning.light',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'warning.main'
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: 'warning.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Devices /> DEVICE REGISTRATION CODE
            </Typography>

            <Paper
              elevation={2}
              sx={{
                p: 2,
                mt: 2,
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                position: 'relative'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  letterSpacing: '0.5px'
                }}
              >
                {deviceCode}
              </Typography>

              <IconButton
                onClick={() => copyToClipboard(deviceCode)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Paper>

            <Alert severity="info" sx={{ mb: 0 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Instructions:
              </Typography>
              1. Copy the device code above<br />
              2. Contact your system administrator<br />
              3. Provide them with this code<br />
              4. Wait for them to register your device<br />
              5. Click "Check Again" to continue
            </Alert>
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> This security measure protects sensitive administrative functions.
              Device registration ensures that only authorized devices can access the system.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={24} /> : <Refresh />}
            size="large"
            sx={{ mr: 2 }}
          >
            {loading ? 'Checking...' : 'Check Again'}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={onLogout}
            disabled={loading}
            startIcon={<ArrowBack />}
            size="large"
          >
            Back to Login
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Device code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

// ============================================
// Main LoginCheckResult Component - Result Verification Page
// ============================================
const LoginCheckResult: React.FC = () => {
  const [isInternet, setIsInternet] = useState({ openAlert: false, message: "" });
  const [status, setStatus] = useState({ loading: false, error: false });
  const authInitState = useAuthInit();
  const history = useHistory();

  const { setSelectedBusiness } = useBusinessContext();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [isReleased, setIsReleased] = useState(true); // default to true to prevent flickering before load
  const [checkingRelease, setCheckingRelease] = useState(true);

  // Fetch release status from backend on component mount
  useEffect(() => {
    const fetchReleaseStatus = async () => {
      try {
        const response = await axios.post(`${baseUrl()}results/isReleased`, {}, config);
        if (response.data && response.data.status === true && response.data.data) {
          setIsReleased(response.data.data.released);
        } else {
          // If API doesn't return properly, fallback to true or handle as needed
          setIsReleased(true);
        }
      } catch (err) {
        console.error("Failed to fetch release status:", err);
        setIsReleased(true); // Fallback so we don't accidentally block everyone on error
      } finally {
        setCheckingRelease(false);
      }
    };
    fetchReleaseStatus();
  }, []);

  // Device Check States
  const [deviceCheckDialogOpen, setDeviceCheckDialogOpen] = useState(false);
  const [deviceCheckLoading, setDeviceCheckLoading] = useState(false);
  const [deviceCode, setDeviceCode] = useState<string>('');
  const [deviceHash, setDeviceHash] = useState<string>('');
  const [userData, setUserData] = useState<any>(null);
  const [userDataHash, setUserDataHash] = useState<any>(null);
  const [deviceCheckResult, setDeviceCheckResult] = useState<any>(null);

  // Form validation schema
  const schema = yup.object({
    accessPin: yup.string().required("Access pin is required"),
    examNumber: yup.string().required("Examination number is required"),
  }).required();

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  // Device Check Functions
  const performDeviceCheck = async (userId: string): Promise<boolean> => {
    setDeviceCheckLoading(true);
    try {
      const deviceId = generateDeviceId(userId);
      setDeviceCode(deviceId.base64);
      setDeviceHash(await deviceId.hash);

      const checkResult = await deviceApi.checkDeviceRegistration(userId, deviceId.hash, deviceId.base64);
      setDeviceCheckResult(checkResult);

      if (checkResult.status === true && checkResult.data?.device_registered === true) {
        return true;
      }
      return false;

    } catch (err: any) {
      setDeviceCheckResult({
        status: false,
        data: { device_registered: false }
      });
      return false;
    } finally {
      setDeviceCheckLoading(false);
    }
  };

  const check_device = async (userId: string) => {
    const deviceId = generateDeviceId(userId);
    try {
      const response = await axios.post(
        baseUrl() + "users/check_device",
        {
          user_id: userId,
          device_id: deviceId.base64,
          device_hash: await deviceId.hash,
          requestID: requestID(),
        },
        config
      );
      return response.data;
    } catch (err: any) {
      return false;
    }
  };

  const handleRetryDeviceCheck = async () => {
    if (!userDataHash) return;
    const isRegistered = await performDeviceCheck(userDataHash.user_id);
    if (isRegistered) {
      setDeviceCheckDialogOpen(false);
      setUserSession(userData.access_token, userData);
      setBusinessData(userData.businessData);
      setSelectedBusiness(userData.businessData);
      authInitState.isLoggedIn = true;
      window.location.replace("/login");
    }
  };

  const handleLogoutFromDeviceCheck = () => {
    setDeviceCheckDialogOpen(false);
    setUserData(null);
    setDeviceCheckResult(null);
    setStatus({ loading: false, error: false });
  };

  // Handle result verification
  const handleVerifyResult = async (data: any) => {
    setStatus({ loading: true, error: false });

    // Check if results have been released globally based on server state
    if (!isReleased) {
      setStatus({ loading: false, error: true });
      setIsInternet({
        openAlert: true,
        message: "Results have not been released yet. Please check back later.",
      });
      return;
    }

    try {
      // API call for result verification
      const response = await axios.post(
        baseUrl() + "results/checkResult",
        {
          accessPin: data.accessPin,
          studentNumber: data.examNumber,
        },
        config
      );

      if (response.data.status === true) {
        // Navigate directly to result display page
        history.push("/result-display", { 
          result: response.data.data,
          accessPin: data.accessPin,
          studentNumber: data.examNumber
        });
        return;
      }

      setStatus({ loading: false, error: true });
      setIsInternet({
        openAlert: true,
        message: response.data.message || "Verification failed. Please try again.",
      });

    } catch (error: any) {
      setStatus({ loading: false, error: true });
      setIsInternet({
        openAlert: true,
        message: error.response?.data?.message || "An error occurred.",
      });
    }
  };

  // Handle return to services
  const handleReturnToServices = () => {
    history.push("/login");
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          {/* Device Check Dialog */}
          <DeviceCheckDialog
            open={deviceCheckDialogOpen}
            deviceCode={deviceCode}
            deviceHash={deviceHash}
            loading={deviceCheckLoading}
            onRetry={handleRetryDeviceCheck}
            onLogout={handleLogoutFromDeviceCheck}
          />

          {/* Full page layout */}
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching DeltaGovPortal */}
            <Header>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LogoCircle>⚖️</LogoCircle>
                <Box>
                  <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                    DELTA STATE GOVERNMENT
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                    Ministry of Primary Education
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    Official Portal
                  </Typography>
                </Box>
              </Box>
            </Header>

            {/* MAIN CONTENT - Centered Result Verification Card */}
            <Box sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              py: { xs: 4, md: 6 },
            }}>
              <StyledCard>
                <Box sx={{ p: 3 }}>
                  {/* Return Link */}
                  <Button
                    onClick={handleReturnToServices}
                    startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                    sx={{
                      color: "#243b5a",
                      fontSize: "13px",
                      textTransform: "none",
                      p: 0,
                      mb: 2,
                      '&:hover': {
                        textDecoration: "underline",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Return to Services
                  </Button>

                  {checkingRelease ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Checking system status...
                      </Typography>
                    </Box>
                  ) : isReleased ? (
                    <>
                      {/* Title */}
                      <Typography variant="h4" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "#1f2937", mb: 0.5 }}>
                        Primary Examination Result Verification
                      </Typography>

                      {/* Description */}
                      <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.5, mb: 2.5 }}>
                        Enter the required credentials to retrieve official examination results
                      </Typography>

                      {/* Divider */}
                      <Divider sx={{ mx: -3, mb: 2.5, borderWidth: 1 }} />

                      {/* Form */}
                      <Box component="form" onSubmit={handleSubmit(handleVerifyResult)}>
                        {/* Access Pin Field */}
                        <StyledFormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                          <InputLabel
                            htmlFor="accessPin"
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            Enter access pin
                          </InputLabel>
                          <OutlinedInput
                            id="accessPin"
                            {...register("accessPin")}
                            placeholder="Enter access pin"
                            error={!!errors.accessPin}
                            startAdornment={
                              <InputAdornment position="start">
                                <VpnKey sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            }
                            sx={{
                              height: "42px",
                              backgroundColor: "#f9fafb",
                              fontSize: "14px",
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#d1d5db",
                                borderRadius: "6px",
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#d1d5db",
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#243b5a",
                                borderWidth: "1px",
                              },
                            }}
                          />
                          {errors.accessPin && (
                            <FormHelperText error>{errors.accessPin.message}</FormHelperText>
                          )}
                        </StyledFormControl>

                        {/* Examination Number Field */}
                        <StyledFormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                          <InputLabel
                            htmlFor="examNumber"
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            Enter examination number
                          </InputLabel>
                          <OutlinedInput
                            id="examNumber"
                            {...register("examNumber")}
                            placeholder="Enter examination number"
                            error={!!errors.examNumber}
                            startAdornment={
                              <InputAdornment position="start">
                                <Assignment sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            }
                            sx={{
                              height: "42px",
                              backgroundColor: "#f9fafb",
                              fontSize: "14px",
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#d1d5db",
                                borderRadius: "6px",
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#d1d5db",
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: "#243b5a",
                                borderWidth: "1px",
                              },
                            }}
                          />
                          {errors.examNumber && (
                            <FormHelperText error>{errors.examNumber.message}</FormHelperText>
                          )}
                        </StyledFormControl>

                        {/* Verify Button */}
                        <VerifyButton
                          type="submit"
                          fullWidth
                          disabled={status.loading}
                        >
                          {status.loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "VERIFY RESULT"}
                        </VerifyButton>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <LockOutlined sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
                      <Typography variant="h5" sx={{ fontWeight: 600, color: "#1f2937", mb: 1.5 }}>
                        Results Not Available
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#6b7280", lineHeight: 1.6 }}>
                        The Primary Examination Results have not been released yet. Please check back later.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </StyledCard>
            </Box>

            {/* Disclaimer */}
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                color: "#9ca3af",
                textAlign: "center",
                maxWidth: "500px",
                mx: "auto",
                px: 2,
                mb: 2,
                display: "block",
              }}
            >
              This is a restricted access portal. Unauthorized access attempts are logged and may be subject to legal action.
            </Typography>

            {/* FOOTER - Matching DeltaGovPortal exactly */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* Loading & Alert */}
          <IonLoading isOpen={status.loading} message="Verifying..." />
          <IonAlert
            isOpen={isInternet.openAlert}
            header="Verification Alert"
            message={isInternet.message}
            buttons={['OK']}
            onDidDismiss={() => setIsInternet({ openAlert: false, message: "" })}
          />
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default LoginCheckResult;